import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PPUIGrid } from 'pricing-pages-ui-components/grid/PPUIGrid';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { getAccountDashboardUrl } from 'self-service-api/core/utilities/links';
import { HighlightedProductsHeaderConfig } from '../../constants/merchCards/HighlightedProductsHeaderConfig';
import { SCREENS } from '../../constants/tracking/Screens';
import { MERCH_CARD } from '../../constants/tracking/TrackingSources';
import HubProductCTA from '../../features/purchaseMotions/components/HubProductCTA';
import MerchCardCTAOverridesWrapper from '../../features/purchaseMotions/components/MerchCardCTAOverridesWrapper';
import { TalkToSalesFallback } from '../../features/purchaseMotions/components/TalkToSalesFallback';
import { useHubPurchaseMotions } from '../../features/purchaseMotions/hooks/useHubPurchaseMotions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useCurrentTrial } from '../../hooks/useActiveTrials';
import { useAppSelector } from '../../reducers';
import { isMerchCardCTAsDataLoadingSelector } from '../../selectors/LoadingStateSelectors';
import { getIsLatestVersionOfProductInProductInventorySelector, hasProductVersionInProductInventorySelector, ownedBundleProductContainingHubSelector } from '../../selectors/ProductInventorySelectors';
import { productSelector, productVersionSelector } from '../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import { shouldShowTermSelectorForMerchSelector } from '../../selectors/TermSelectors';
import { useFTS0012Experiment } from '../../utils/experiments/fts0012';
import { useSSM0009Experiment } from '../../utils/experiments/ssm0009';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import ExploreFreeToolsButton from '../buttons/ExploreFreeToolsButton';
import ProductIcon from '../common/ProductIcon';
import DetailedTermSelector from '../common/TermSelectors/DetailedTermSelector';
import CurrentPlanTag from '../common/badges/CurrentPlanTag';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import AdditionalUnitsPickerCompactAll from '../common/quantityPacks/AdditionalUnitsPickerCompactAll';
import ErrorBoundary from '../error-boundary/ErrorBoundary';
import RequiredOnboardingSubtextContainer from '../plan-builder/RequiredOnboardingSubtextContainer';
import { MerchCardTitleContainer, MerchCardWrapper } from './MerchCardSharedComponents';
import BusinessObjectivesContainer from './business-objectives/BusinessObjectivesContainer';
import HighlightedMerchPriceDisplay from './price-display/HighlightedMerchPriceDisplay';
import { useSkuAdditionalUnits } from './useSkuAdditionalUnits';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HighlightedMerchCard = ({
  productLevel,
  productLine,
  selectedTab,
  gridItemProps
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const isAppReady = useGlobalLoading();
  const isMerchCardCTAsDataLoading = useSelector(isMerchCardCTAsDataLoadingSelector);
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const {
    isFTS0012Variant
  } = useFTS0012Experiment();
  const apiName = product && product.apiName;
  const currentTrial = useCurrentTrial(apiName);
  const seleniumId = apiName || 'loading';
  const {
    additionalUnitSkuId,
    isAdditionalUnitSkuIdAQuantityPack,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    quantityPackApiName,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const ownsProductVersion = useAppSelector(state => isAppReady && hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const isCurrentPlan = useAppSelector(state => isAppReady && getIsLatestVersionOfProductInProductInventorySelector(state, {
    productIdentifier: {
      apiName
    }
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const [isLoadingQuantityPacks, setIsLoadingQuantityPacks] = useState(false);
  const {
    title = '',
    subtitle = '',
    productLevelTitle = ''
  } = HighlightedProductsHeaderConfig[productLine][productLevel] || {};
  const shouldShowTermSelector = useAppSelector(state => {
    if (!isAppReady || !skuId) return false;
    return shouldShowTermSelectorForMerchSelector(state, {
      skuId
    });
  });
  const additionalUnitsPickerProps = {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    productConfigurations: [createProductConfig({
      skuId: skuId || 0,
      quantity: 1
    })],
    queryParamTerm,
    trackingSource: MERCH_CARD,
    productLevel,
    isLoadingQuantityPacks
  };
  const ownedBundleProductContainingHub = useAppSelector(state => {
    if (!isAppReady) return;
    return ownedBundleProductContainingHubSelector(state, {
      skuId
    });
  });
  const bundleType = useAppSelector(state => {
    if (!ownedBundleProductContainingHub) return;
    return productVersionSelector(state, {
      skuId: ownedBundleProductContainingHub.skuId
    }).bundleType;
  });
  const purchaseMotions = useHubPurchaseMotions({
    skuId
  });
  const ownsProductViaBundle = !!ownedBundleProductContainingHub;
  const {
    isSSM0009Variant
  } = useSSM0009Experiment();
  const showExploreFreeToolsButton = isSSM0009Variant && purchaseMotions.length === 1 && (product === null || product === void 0 ? void 0 : product.productLevel) === STARTER;
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });

  // In order for the grid to work, the wrapper element requires display: contents.
  // Children of the display grid item must get the grid-start attributes directly
  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    children: [/*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      gridRowStart: 1,
      paddingBlockStart: "800",
      position: "relative",
      "data-selenium-id": `${seleniumId}-merch-card`,
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [isCurrentPlan && /*#__PURE__*/_jsx(CurrentPlanTag, {
          message: "pricingPage.highlightedMerchCards.currentPlan"
        }), ownsProductViaBundle && /*#__PURE__*/_jsx(CurrentPlanTag, {
          message: `pricingPage.highlightedMerchCards.youOwnViaBundle.${bundleType}.${productLevel}`
        }), currentTrial && /*#__PURE__*/_jsx(CurrentPlanTag, {
          message: "pricingPage.highlightedMerchCards.currentTrial"
        }), /*#__PURE__*/_jsx(MerchCardTitleContainer, {
          children: isFTS0012Variant ? /*#__PURE__*/_jsx(SectionTitle, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: productLevelTitle
            })
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(ProductIcon, {
              name: productLine,
              tier: productLevel,
              iconStyle: {
                responsive: false,
                blockSize: '24px'
              }
            }), /*#__PURE__*/_jsx(SectionTitle, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: title
              })
            })]
          })
        })]
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      gridRowStart: 2,
      paddingBlockStart: "600",
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: isSeatsRedesignUngated && ownsProductVersion ? /*#__PURE__*/_jsx(Link, {
          href: getAccountDashboardUrl(),
          "data-selenium-id": `${MERCH_CARD}-${apiName}-viewAccount-button`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.viewAccountButton.text"
          })
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: subtitle
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 3,
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          heights: [20, 33, 20],
          widths: [60, 80, 140],
          children: /*#__PURE__*/_jsx(HighlightedMerchPriceDisplay, {
            queryParamTerm: queryParamTerm,
            skuId: skuId,
            quantityPackSkuId: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitSkuId : undefined,
            quantityPackQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0,
            quantityPackApiName: quantityPackApiName,
            apiName: apiName,
            setIsLoadingQuantityPacks: setIsLoadingQuantityPacks
          })
        })
      })
    })), shouldShowTermSelector && /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 4,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [41],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(DetailedTermSelector, {
          apiName: apiName,
          onChange: setQueryParamTerm,
          activeTerm: queryParamTerm,
          trackingSource: MERCH_CARD
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 5,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [34, 76],
        widths: ['75%', '100%'],
        children: /*#__PURE__*/_jsx(AdditionalUnitsPickerCompactAll, Object.assign({}, additionalUnitsPickerProps))
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      marginBlockEnd: "500",
      gridRowStart: 6,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isMerchCardCTAsDataLoading,
        heights: [47],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
            apiName: apiName,
            trackingSource: MERCH_CARD,
            screen: SCREENS[productLine]
          }),
          children: /*#__PURE__*/_jsx(MerchCardCTAOverridesWrapper, Object.assign({
            skuId: skuId,
            trackingSource: MERCH_CARD
          }, ctaVariantProps, {
            children: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              justifyContent: "center",
              gap: "200",
              inlineSize: "100%",
              children: [purchaseMotions.map((purchaseMotion, index) => {
                return /*#__PURE__*/_jsx("div", {
                  children: /*#__PURE__*/_jsx(HubProductCTA, Object.assign({
                    purchaseMotion: purchaseMotion,
                    skuId: skuId,
                    productLine: productLine,
                    additionalUnitSkuId: additionalUnitSkuId,
                    additionalUnitQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0,
                    trackingSource: MERCH_CARD,
                    use: index === 0 ? 'primary' : 'secondary',
                    purchaseMotionLocation: 'MERCH_CARD'
                  }, ctaVariantProps))
                }, purchaseMotion);
              }), showExploreFreeToolsButton && /*#__PURE__*/_jsx(ExploreFreeToolsButton, {
                trackingSource: MERCH_CARD,
                use: "secondary"
              })]
            })
          }))
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGrid, Object.assign({}, gridItemProps, {
      gridTemplateRows: "subgrid",
      gridRowStart: 7,
      gridRowEnd: 9,
      children: /*#__PURE__*/_jsx(BusinessObjectivesContainer, {
        productLine: productLine,
        productLevel: productLevel,
        apiName: apiName
      })
    })), skuId ? /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      gridRowStart: 9,
      paddingBlockStart: "400",
      paddingBlockEnd: "600",
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        alignItems: "center",
        children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
          queryParamTerm: queryParamTerm,
          skuIds: [skuId],
          tab: selectedTab,
          trackingSource: MERCH_CARD,
          includesOnboardingCost: false
        })
      })
    })) : null]
  });
};
export default HighlightedMerchCard;